// src/redux/userSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = "https://apis.pluglr.com/pluglr";

// Async thunk for fetching Home data
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      const response = await axios.get(`${BASE_URL}/api/v1/auth/users/get`, {
        headers: {
          Authorization: `${token}`,
          "X-From": "web",
          "Content-Type": "application/json",
        },
      });
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const fetchUsersImage = createAsyncThunk(
  "users/fetchUsersImage",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken");
      const response = await axios.get(
        "https://apis.pluglr.com/pluglr/api/v1/auth/users/images/download?time=" +
          new Date().getTime(),
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
          },
          responseType: "blob", // 👈 THIS IS THE FIX
        }
      );

      // Create a temporary URL for the image
      const imageUrl = URL.createObjectURL(response.data);
      return imageUrl; // return a ready-to-use URL
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// Fetch user details by ID
export const fetchUserById = createAsyncThunk(
  "users/fetchUserById",
  async (userId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken");
      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/users/fetch/${userId}`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

export const uploadUsersImage = createAsyncThunk(
  "users/uploadUsersImage",
  async (file, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const formData = new FormData();
      formData.append("image", file); // ✅ only the image

      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/users/images/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data; // e.g. { url: "...", message: "..." }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to upload image"
      );
    }
  }
);

export const fetchScheduleCategory = createAsyncThunk(
  "users/fetchScheduleCategory",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      console.log(token);

      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/events/categories/list`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const fetchOngoingEvents = createAsyncThunk(
  "users/fetchOngoingEvents",
  async ({ page = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken");

      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/events/list/ongoing/${page}/${pageSize}`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const fetchUpcomingEvents = createAsyncThunk(
  "users/fetchUpcomingEvents",
  async ({ page = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken");

      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/events/list/upcoming/${page}/${pageSize}`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const fetchAllUserEvents = createAsyncThunk(
  "users/fetchAllUserEvents",
  async ({ userId, page = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      console.log(token);

      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/events/list/foruser/${userId}/${page}/${pageSize}`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const createEvent = createAsyncThunk(
  "events/createEvent",
  async (eventData, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");

      // Remove "Bearer " prefix if it already exists in localStorage
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/events/create`,
        eventData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Only one Bearer prefix now
            "Content-Type": "application/json",
            "X-From": "web",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchEventImage = createAsyncThunk(
  "events/fetchEventImage",
  async (evtId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken");
      const response = await axios.get(
        `https://apis.pluglr.com/pluglr/api/v1/auth/events/images/fetch/byEvent/${evtId}`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
          },
          responseType: "blob", // <-- important for images
        }
      );

      // Convert blob to local URL
      const imageUrl = URL.createObjectURL(response.data);
      return { evtId, imageUrl };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch event image"
      );
    }
  }
);

export const updateEvent = createAsyncThunk(
  "events/updateEvent",
  async (eventData, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");

      // Remove "Bearer " prefix if it already exists in localStorage
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.put(
        `${BASE_URL}/api/v1/auth/events/update/all`,
        eventData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Only one Bearer prefix now
            "Content-Type": "application/json",
            "X-From": "web",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchSpecificEvent = createAsyncThunk(
  "users/fetchSpecificEvent",
  async (eventId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      console.log(token);

      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/events/get/${eventId}`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// Channels Apis

export const createUserChanels = createAsyncThunk(
  "events/createUserChanels",
  async (channelData, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");

      // Remove "Bearer " prefix if it already exists in localStorage
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/channels/create`,
        channelData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Only one Bearer prefix now
            "Content-Type": "application/json",
            "X-From": "web",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchAllChannels = createAsyncThunk(
  "users/fetchAllChannels",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/channels/list`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const fetchSpecificChannel = createAsyncThunk(
  "users/fetchSpecificChannel",
  async (channelId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      console.log(token);

      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/channels/get/${channelId}`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const fetchChannelForAnotherUser = createAsyncThunk(
  "users/fetchChannelForAnotherUser",
  async (userId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      console.log(token);

      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/channels/listfor/${userId}`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// Moderator Apis
export const addModerator = createAsyncThunk(
  "events/addModerator",
  async ({ channelId, userId }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");

      // Remove "Bearer " prefix if it already exists in localStorage
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/channels/moderators/add/${channelId}/${userId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-From": "web",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchChannelModerator = createAsyncThunk(
  "users/fetchChannelModerator",
  async (channelId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      console.log(token);

      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/channels/moderators/list/${channelId}`,
        {
          headers: {
            Authorization: `${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const removeModerator = createAsyncThunk(
  "moderators/removeModerator",
  async ({ channelId, userId }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.delete(
        `${BASE_URL}/api/v1/auth/channels/moderators/remove/${channelId}/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
            "Content-Type": "application/json",
          },
        }
      );
      return { channelId, userId, message: response.data?.message };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to remove moderator"
      );
    }
  }
);

export const uploadEventImage = createAsyncThunk(
  "eventImages/upload",
  async ({ evt_id, file }, { rejectWithValue }) => {
    try {
      // Get token from localStorage and clean it up
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      // Prepare FormData with both evt_id and image
      const formData = new FormData();
      formData.append("evt_id", evt_id);
      formData.append("image", file);

      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/events/images/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data; // Expecting { url: "...", message: "..." }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to upload image"
      );
    }
  }
);

// Plans

export const fetchAllPlans = createAsyncThunk(
  "users/fetchAllPlans",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      const response = await axios.get(`${BASE_URL}/api/v1/auth/plans/list`, {
        headers: {
          Authorization: `${token}`,
          "X-From": "web",
          "Content-Type": "application/json",
        },
      });
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const fetchPaystackKeys = createAsyncThunk(
  "payments/fetchPaystackKeys",
  async (_, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.get(`${BASE_URL}/api/v1/auth/payments/keys`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-From": "web",
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Unable to fetch Paystack keys");
    }
  }
);

export const createPaymentRecord = createAsyncThunk(
  "payments/createPaymentRecord",
  async (payload, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/payments/create`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-From": "web",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Unable to create payment record");
    }
  }
);

export const makeaPlan = createAsyncThunk(
  "events/makeaPlan",
  async (propData, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");

      // Remove "Bearer " prefix if it already exists in localStorage
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }

      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/plans/create`,
        propData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Only one Bearer prefix now
            "Content-Type": "application/json",
            "X-From": "web",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchMyPlans = createAsyncThunk(
  "users/fetchMyPlans",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("pluglrUserToken"); // or whatever key you're using
      const response = await axios.get(`${BASE_URL}/api/v1/auth/plans/get`, {
        headers: {
          Authorization: `${token}`,
          "X-From": "web",
          "Content-Type": "application/json",
        },
      });
      return response.data; // Adjust this if the data structure is nested
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// Add Comment
export const addComment = createAsyncThunk(
  "events/addComment",
  async ({ eventId, text }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/events/comments/add`,
        { eventId, text },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-From": "web",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to add comment");
    }
  }
);

// Fetch Comments
export const fetchComments = createAsyncThunk(
  "events/fetchComments",
  async (
    { eventId, page = 0, size = 10, sortOrder = "desc" },
    { rejectWithValue }
  ) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/events/comments/list/${eventId}?page=${page}&size=${size}&sortOrder=${sortOrder}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
          },
        }
      );
      return { data: response.data, page };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch comments"
      );
    }
  }
);

// Add Reaction
export const addReaction = createAsyncThunk(
  "events/addReaction",
  async ({ commentId, reaction }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/events/comments/reactions/add`,
        { commentId, reaction },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-From": "web",
          },
        }
      );
      return { commentId, reaction, response: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to add reaction");
    }
  }
);

// Remove Reaction
export const removeReaction = createAsyncThunk(
  "events/removeReaction",
  async (commentId, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.delete(
        `${BASE_URL}/api/v1/auth/events/comments/reactions/remove/${commentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-From": "web",
          },
        }
      );
      return { commentId, response: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to remove reaction"
      );
    }
  }
);

// Fetch Reaction Counts
export const fetchReactionCounts = createAsyncThunk(
  "events/fetchReactionCounts",
  async (commentId, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/events/comments/reactions/count/${commentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
          },
        }
      );
      return { commentId, counts: response.data?.data || response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch reaction counts"
      );
    }
  }
);

export const checkCallBlockStatus = createAsyncThunk(
  "moderation/checkCallBlockStatus",
  async ({ eventId, userId }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/channels/moderation/blocked/fromcalls/event/${eventId}/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to check call block status"
      );
    }
  }
);

export const checkChatBlockStatus = createAsyncThunk(
  "moderation/checkChatBlockStatus",
  async ({ eventId, userId }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/channels/moderation/blocked/fromchat/event/${eventId}/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to check chat block status"
      );
    }
  }
);

export const flagComment = createAsyncThunk(
  "moderation/flagComment",
  async ({ commentId, flagType, specificReason }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.post(
        `${BASE_URL}/api/v1/auth/events/comments/flags/create`,
        {
          commentId,
          flag: flagType, // Backend expects 'flag', not 'flagType'
          specificReason,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to flag comment");
    }
  }
);

export const unflagComment = createAsyncThunk(
  "moderation/unflagComment",
  async (commentId, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.delete(
        `${BASE_URL}/api/v1/auth/events/comments/flags/remove/${commentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to unflag comment"
      );
    }
  }
);

export const fetchCommentFlags = createAsyncThunk(
  "moderation/fetchCommentFlags",
  async ({ commentId, page = 0, pageSize = 10 }, { rejectWithValue }) => {
    try {
      let token = localStorage.getItem("pluglrUserToken");
      if (token?.startsWith("Bearer ")) {
        token = token.replace("Bearer ", "");
      }
      const response = await axios.get(
        `${BASE_URL}/api/v1/auth/events/comments/flags/list/${commentId}?page=${page}&pageSize=${pageSize}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-From": "web",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch comment flags"
      );
    }
  }
);

const userSlice = createSlice({
  name: "features",
  initialState: {
    data: null,
    anotherUserChannel: null,
    userData: null,
    userChannel: null,
    eventSuccessTwo: null,
    moderatorloading: false,
    removeModeratorloading: false,
    removeModerator: null,
    specificEvent: null,
    userAllChannels: null,
    sheduleCategory: null,
    channelModerator: null,
    planSuccess: null,
    planloading: false,
    paystackKeys: null,
    paymentLoading: false,
    paymentSuccess: null,
    paymentError: null,

    // Comments State
    comments: { content: [] },
    commentsLoading: false,
    commentsError: null,
    commentsPage: 0,
    commentsTotal: 0,
    commentsHasMore: true,

    // Payment
    userWallet: [],
    userEvents: null,
    userImage: null,
    eventImages: {},
    moderatorSuccess: null,
    eventSuccess: null,
    upcomingEventsLoading: false,
    upcomingEvents: null,
    eventLoading: false,
    channelloading: false,
    userMyPlans: null,
    userimageUrl: null,
    imageloading: false,
    channelSuccess: null,
    userAllPlans: null,
    imageUrl: null,
    eventSuccessMessage: null,
    loading: false,
    ongoingEvents: null,
    ongoingEventsLoading: false,
    error: null,
  },
  reducers: {
    clearComments: (state) => {
      state.comments = { content: [], totalElements: 0 };
      state.commentsPage = 0;
      state.commentsHasMore = true;
    },
    receiveComment: (state, action) => {
      const socketData = action.payload;
      if (state.comments && state.comments.content) {
        // Prepend new comment if it doesn't already exist
        const commentId = socketData.id || socketData._id;
        const exists = state.comments.content.some(
          (c) => String(c.id || c._id) === String(commentId)
        );
        if (!exists) {
          // Normalize to match fetch structure (reactionCounts, userReaction)
          const comment = {
            ...socketData,
            reactionCounts: socketData.reactionCounts || {},
            userReaction: socketData.userReaction || "NOT_SET",
          };
          state.comments.content = [comment, ...state.comments.content];
          state.comments.totalElements =
            (state.comments.totalElements || 0) + 1;
        }
      }
    },
    receiveReactionAdd: (state, action) => {
      const { commentId, reactionType, userId, previousReactionType } =
        action.payload;

      // Deduplication: Ignore if we processed this exact event recently
      const dedupKey = `${commentId}_${userId}_${reactionType}`;
      const now = Date.now();
      const lastProcessed = state._recentReactionEvents?.[dedupKey];

      if (lastProcessed && now - lastProcessed < 2000) {
        console.warn("Ignoring duplicate reaction event:", dedupKey);
        return;
      }

      // Initialize cache if missing
      if (!state._recentReactionEvents) {
        state._recentReactionEvents = {};
      }
      state._recentReactionEvents[dedupKey] = now;

      // Cleanup old cache entries occasionally (simple approach: clear if too big)
      if (Object.keys(state._recentReactionEvents).length > 100) {
        state._recentReactionEvents = {};
      }

      if (state.comments && state.comments.content) {
        state.comments.content = state.comments.content.map((comment) => {
          const id = comment.id || comment._id;
          if (String(id) === String(commentId)) {
            const counts = { ...(comment.reactionCounts || {}) };
            console.log("Reducer: receiveReactionAdd", {
              commentId,
              reactionType,
              previousReactionType,
              countsBefore: { ...counts },
            });
            if (previousReactionType && counts[previousReactionType] > 0) {
              counts[previousReactionType]--;
            }
            counts[reactionType] = (counts[reactionType] || 0) + 1;
            console.log("Reducer: receiveReactionAdd", {
              countsAfter: { ...counts },
            });

            // If this reaction is from the current user, update their local view too
            const currentUserId =
              state.userData?.id || state.userData?.data?.id;
            const updatedUserReaction =
              String(userId) === String(currentUserId)
                ? reactionType
                : comment.userReaction;

            return {
              ...comment,
              reactionCounts: counts,
              userReaction: updatedUserReaction,
            };
          }
          return comment;
        });
      }
    },
    receiveReactionRemove: (state, action) => {
      const { commentId, reactionType, userId } = action.payload;

      // Deduplication for remove
      const dedupKey = `${commentId}_${userId}_REMOVE_${reactionType}`;
      const now = Date.now();
      const lastProcessed = state._recentReactionEvents?.[dedupKey];

      if (lastProcessed && now - lastProcessed < 2000) {
        return;
      }

      if (!state._recentReactionEvents) {
        state._recentReactionEvents = {};
      }
      state._recentReactionEvents[dedupKey] = now;

      if (state.comments && state.comments.content) {
        state.comments.content = state.comments.content.map((comment) => {
          const id = comment.id || comment._id;
          if (String(id) === String(commentId)) {
            const counts = { ...(comment.reactionCounts || {}) };
            counts[reactionType] = Math.max(0, (counts[reactionType] || 0) - 1);

            // If this removal is from the current user, update their local view too
            const currentUserId =
              state.userData?.id || state.userData?.data?.id;
            const updatedUserReaction =
              String(userId) === String(currentUserId)
                ? "NOT_SET"
                : comment.userReaction;

            return {
              ...comment,
              reactionCounts: counts,
              userReaction: updatedUserReaction,
            };
          }
          return comment;
        });
      }
    },
    resetCreateEvent: (state) => {
      state.eventSuccess = null;
    },
    resetUserImageUpload: (state) => {
      state.userimageUrl = null;
    },
    resetCreatePlan: (state) => {
      state.planSuccess = null;
    },
    clearEventImageState: (state) => {
      state.imageUrl = null;
    },
    resetCreateChannels: (state) => {
      state.channelSuccess = null;
    },
    resetCreateModerators: (state) => {
      state.moderatorSuccess = null;
    },

    resetRemoveModerators: (state) => {
      state.removeModerator = null;
    },
  }, // No synchronous reducers
  extraReducers: (builder) => {
    builder
      // Fetch Home Data
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchPaystackKeys.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
      })
      .addCase(fetchPaystackKeys.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.paystackKeys = action.payload;
      })
      .addCase(fetchPaystackKeys.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
      })
      .addCase(createPaymentRecord.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
        state.paymentSuccess = null;
      })
      .addCase(createPaymentRecord.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.paymentSuccess = action.payload;
      })
      .addCase(createPaymentRecord.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchUsersImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsersImage.fulfilled, (state, action) => {
        state.loading = false;
        state.userImage = action.payload;
      })
      .addCase(fetchUsersImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchEventImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEventImage.fulfilled, (state, action) => {
        const { evtId, imageUrl } = action.payload;
        state.eventImages[evtId] = imageUrl; // save in Redux
      })
      .addCase(fetchEventImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchScheduleCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScheduleCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.sheduleCategory = action.payload;
      })
      .addCase(fetchScheduleCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchOngoingEvents.pending, (state) => {
        state.ongoingEventsLoading = true;
        state.error = null;
      })
      .addCase(fetchOngoingEvents.fulfilled, (state, action) => {
        state.ongoingEventsLoading = false;
        state.ongoingEvents = action.payload;
      })
      .addCase(fetchOngoingEvents.rejected, (state, action) => {
        state.ongoingEventsLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchUpcomingEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUpcomingEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.upcomingEvents = action.payload;
      })
      .addCase(fetchUpcomingEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchAllUserEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUserEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.userEvents = action.payload;
      })
      .addCase(fetchAllUserEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchSpecificEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSpecificEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.specificEvent = action.payload;
      })
      .addCase(fetchSpecificEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(createEvent.pending, (state) => {
        state.eventloading = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.eventloading = false;
        state.eventSuccess = action.payload;
        state.eventSuccessTwo = action.payload;
        state.eventSuccessMessage = action?.payload?.message;
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.eventloading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updateEvent.pending, (state) => {
        state.eventloading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.eventloading = false;
        state.eventSuccess = action.payload;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.eventloading = false;
        state.error = action.payload;
      });

    builder
      .addCase(uploadEventImage.pending, (state) => {
        state.eventloading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(uploadEventImage.fulfilled, (state, action) => {
        state.eventloading = false;
        state.imageUrl = action.payload;
      })
      .addCase(uploadEventImage.rejected, (state, action) => {
        state.eventloading = false;
        state.error = action.payload || "Error uploading image";
      });

    builder
      .addCase(uploadUsersImage.pending, (state) => {
        state.imageloading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(uploadUsersImage.fulfilled, (state, action) => {
        state.imageloading = false;
        state.userimageUrl = action.payload;
      })
      .addCase(uploadUsersImage.rejected, (state, action) => {
        state.imageloading = false;
        state.error = action.payload || "Error uploading image";
      });

    // Channels

    builder
      .addCase(createUserChanels.pending, (state) => {
        state.channelloading = true;
        state.error = null;
      })
      .addCase(createUserChanels.fulfilled, (state, action) => {
        state.channelloading = false;
        state.channelSuccess = action.payload;
      })
      .addCase(createUserChanels.rejected, (state, action) => {
        state.channelloading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchAllChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllChannels.fulfilled, (state, action) => {
        state.loading = false;
        state.userAllChannels = action.payload;
      })
      .addCase(fetchAllChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchSpecificChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSpecificChannel.fulfilled, (state, action) => {
        state.loading = false;
        state.userChannel = action.payload;
      })
      .addCase(fetchSpecificChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchChannelForAnotherUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChannelForAnotherUser.fulfilled, (state, action) => {
        state.loading = false;
        state.anotherUserChannel = action.payload;
      })
      .addCase(fetchChannelForAnotherUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Moderators
    builder
      .addCase(addModerator.pending, (state) => {
        state.moderatorloading = true;
        state.error = null;
      })
      .addCase(addModerator.fulfilled, (state, action) => {
        state.moderatorloading = false;
        state.moderatorSuccess = action.payload;
      })
      .addCase(addModerator.rejected, (state, action) => {
        state.moderatorloading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchChannelModerator.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChannelModerator.fulfilled, (state, action) => {
        state.loading = false;
        state.channelModerator = action.payload;
      })
      .addCase(fetchChannelModerator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(removeModerator.pending, (state) => {
        state.removeModeratorloading = true;
        state.error = null;
      })
      .addCase(removeModerator.fulfilled, (state, action) => {
        state.removeModeratorloading = false;
        state.removeModerator = action.payload;
      })
      .addCase(removeModerator.rejected, (state, action) => {
        state.removeModeratorloading = false;
        state.error = action.payload;
      });

    // Plans

    builder
      // Fetch Home Data
      .addCase(fetchAllPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.userAllPlans = action.payload;
      })
      .addCase(fetchAllPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      // Fetch Home Data
      .addCase(fetchMyPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.userMyPlans = action.payload;
      })
      .addCase(fetchMyPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(makeaPlan.pending, (state) => {
        state.planloading = true;
        state.error = null;
      })
      .addCase(makeaPlan.fulfilled, (state, action) => {
        state.planloading = false;
        state.planSuccess = action.payload;
      })
      .addCase(makeaPlan.rejected, (state, action) => {
        state.planloading = false;
        state.error = action.payload;
      });

    // Comments
    builder
      .addCase(addComment.fulfilled, (state, action) => {
        // Optimistically add to list or wait for fetch?
        // Usually better to prepend. Assuming payload return is the new comment or standard success.
        // If data structure is complex, might need to re-fetch.
        // For now, let component handle refetch or just push if structure matches.
      })
      .addCase(fetchComments.pending, (state) => {
        state.commentsLoading = true;
        state.commentsError = null;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.commentsLoading = false;
        const { data, page } = action.payload;

        // The user's response structure shows comments in data.data.content
        const nestedData = data?.data || data;
        const newComments = Array.isArray(nestedData?.content)
          ? nestedData.content
          : Array.isArray(data?.content)
          ? data.content
          : [];

        if (page === 0) {
          // Store the full nestedData so we have access to metadata like 'last', 'totalElements'
          state.comments = { ...nestedData, content: newComments };
        } else {
          state.comments = {
            ...state.comments,
            ...nestedData,
            content: [...(state.comments?.content || []), ...newComments],
          };
        }

        state.commentsPage = page;
        // Use the 'last' property from the API to determine if more pages exist
        state.commentsHasMore = nestedData?.last === false;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.commentsLoading = false;
        state.commentsError = action.payload;
      });

    builder
      .addCase(addReaction.fulfilled, (state, action) => {
        const { commentId, reaction } = action.payload;
        if (state.comments?.content) {
          state.comments.content = state.comments.content.map((comment) => {
            const id = comment.id || comment._id;
            if (String(id) === String(commentId)) {
              // ONLY update the user's reaction status.
              // Counts will be updated via the socket broadcast to avoid double-counting.
              return {
                ...comment,
                userReaction: reaction,
              };
            }
            return comment;
          });
        }
      })
      .addCase(addReaction.rejected, (state, action) => {
        state.error = action.payload;
      });

    builder
      .addCase(removeReaction.fulfilled, (state, action) => {
        const { commentId } = action.payload;
        if (state.comments?.content) {
          state.comments.content = state.comments.content.map((comment) => {
            const id = comment.id || comment._id;
            if (String(id) === String(commentId)) {
              const currentCounts = { ...(comment.reactionCounts || {}) };
              const oldReaction = comment.userReaction;

              return {
                ...comment,
                userReaction: "NOT_SET",
              };
            }
            return comment;
          });
        }
      })
      .addCase(removeReaction.rejected, (state, action) => {
        state.error = action.payload;
      });

    builder
      .addCase(fetchReactionCounts.fulfilled, (state, action) => {
        const { commentId, counts } = action.payload;
        if (state.comments?.content) {
          state.comments.content = state.comments.content.map((comment) => {
            const id = comment.id || comment._id;
            if (String(id) === String(commentId)) {
              return { ...comment, reactionCounts: counts };
            }
            return comment;
          });
        }
      })
      .addCase(fetchReactionCounts.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});
export const {
  resetCreateChannels,
  resetUserImageUpload,
  resetCreatePlan,
  clearEventImageState,
  resetCreateEvent,
  resetCreateModerators,
  resetRemoveModerators,
  clearComments,
  receiveComment,
  receiveReactionAdd,
  receiveReactionRemove,
} = userSlice.actions;

export default userSlice.reducer;
